// CO-2315 Round-2 item #11 - one normalised row of a field-change audit trail.
//
// Three services write this record and two of them already agree on its shape:
// PayerChange (payer-functions payer_config_dto.go) and FacilityChange (facility-functions
// facility_functions_dto.go) are field-for-field identical. ChangesHistory
// (patient-benefits-functions) was the outlier until CO-2315 item #11 Phase 1 gave it
// newValue and the name pair.
//
// This interface is the shape the panel renders. Adapters below map each record onto it, so
// the panel never learns which service a row came from.
export interface ChangeHistoryEntry {
  fieldName: string;
  previousValue: string;
  newValue: string;
  userFirstName: string;
  userLastName: string;
  changeDate: string;
  comment: string;
  // True when newValue was RECONSTRUCTED rather than read off the record.
  //
  // Entries written before item #11 carry no new value, and it can only be inferred from
  // the entry that follows. The inference can be wrong - the audit's own write path is
  // best-effort, so a change can be made and never recorded - which is why the panel shows
  // the difference instead of quietly presenting a guess as a fact.
  newValueDerived?: boolean;
}

// A ChangesHistory row as patient-benefits-functions serializes it.
export interface RawChangesHistory {
  historyId?: number;
  changesMadeTo?: string;
  previousValue?: string;
  newValue?: string;
  changeMadeBy?: string;
  userFirstName?: string;
  userLastName?: string;
  dateOfChange?: string;
  reasonForChange?: string;
  newValueDerived?: boolean;
}

// A PayerChange / FacilityChange row. The two are identical, so one adapter serves both.
export interface RawFieldChange {
  fieldName?: string;
  previousValue?: string;
  newValue?: string;
  userFirstName?: string;
  userLastName?: string;
  changeDate?: string;
  comment?: string;
}

function text(value: any): string {
  return value === null || value === undefined ? "" : String(value);
}

// fromChangesHistory maps the patient-benefits shape onto the panel's.
//
// changeMadeBy is a sign-in address; userFirstName/userLastName are the person. Older rows
// have only the address, so it becomes the first name and the panel renders something
// identifiable rather than a blank author cell - a blank one reads as a system change.
export function fromChangesHistory(rows: RawChangesHistory[]): ChangeHistoryEntry[] {
  return (rows || []).map((r) => {
    const first = text(r.userFirstName);
    const last = text(r.userLastName);
    const named = first !== "" || last !== "";
    return {
      fieldName: text(r.changesMadeTo),
      previousValue: text(r.previousValue),
      newValue: text(r.newValue),
      userFirstName: named ? first : text(r.changeMadeBy),
      userLastName: named ? last : "",
      changeDate: text(r.dateOfChange),
      comment: text(r.reasonForChange),
      newValueDerived: r.newValueDerived === true,
    };
  });
}

// fromFieldChanges maps a PayerChange or FacilityChange onto the panel's shape.
export function fromFieldChanges(rows: RawFieldChange[]): ChangeHistoryEntry[] {
  return (rows || []).map((r) => ({
    fieldName: text(r.fieldName),
    previousValue: text(r.previousValue),
    newValue: text(r.newValue),
    userFirstName: text(r.userFirstName),
    userLastName: text(r.userLastName),
    changeDate: text(r.changeDate),
    comment: text(r.comment),
    newValueDerived: false,
  }));
}

// LABELS are named constants so a Product wording change is a one-line swap rather than a
// hunt through a template - the same discipline item #5 applied to the Aid Code warnings.
// The strings are the ones the shipped facilities-view panel already renders; changing them
// here would change a live screen.
export const CHANGE_HISTORY_LABELS = {
  section: "History",
  fieldName: "Changes Made To",
  previousValue: "Previous Value",
  newValue: "New Value",
  changedBy: "Change Made By",
  changeDate: "Date of Change",
  comment: "Comment",
  empty: "No changes have been recorded.",
  derivedSuffix: "(derived)",
};



// WEBPACK FOOTER //
// ./src/components/change-history-panel/change-history-entry.ts